import { useState, useEffect, useCallback } from 'react';
import {
  getEconomicGroups,
  createEconomicGroup,
  updateEconomicGroup,
  deleteEconomicGroup,
} from '../services/EconomicGroupService';
import { EconomicGroup } from '../types/interfaces';

let economicGroupsRequest: Promise<{ success?: boolean; data?: EconomicGroup[]; message?: string }> | null = null;

export const useEconomicGroups = () => {
  const [economicGroups, setEconomicGroups] = useState<EconomicGroup[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const fetchEconomicGroups = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh && economicGroupsRequest) {
      try {
        const response = await economicGroupsRequest;
        if (response.success && response.data) setEconomicGroups(response.data);
        else if (response.message) setError(response.message);
      } catch {
        setError('Error al cargar los grupos económicos');
      } finally {
        setIsLoading(false);
      }
      return;
    }
    economicGroupsRequest = getEconomicGroups();
    setIsLoading(true);
    setError(null);
    try {
      const response = await economicGroupsRequest;
      if (response.success && response.data) {
        setEconomicGroups(response.data);
      } else {
        const errorMsg = response.message || 'Error al cargar los grupos económicos';
        setError(errorMsg);
      }
    } catch {
      setError('Error al cargar los grupos económicos');
    } finally {
      economicGroupsRequest = null;
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEconomicGroups();
  }, [fetchEconomicGroups]);

  const refreshEconomicGroups = useCallback(() => {
    return fetchEconomicGroups(true);
  }, [fetchEconomicGroups]);

  const createGroup = useCallback(
    async (name: string, code: string): Promise<{ success: boolean; message?: string }> => {
      setIsCreating(true);
      setError(null);
      try {
        const response = await createEconomicGroup(name, code);
        if (response.success) {
          await refreshEconomicGroups();
          return { success: true };
        }
        const errorMsg = response.message || 'Error al crear el grupo económico';
        setError(errorMsg);
        return { success: false, message: errorMsg };
      } catch {
        const errorMsg = 'Error al crear el grupo económico';
        setError(errorMsg);
        return { success: false, message: errorMsg };
      } finally {
        setIsCreating(false);
      }
    },
    [refreshEconomicGroups],
  );

  const updateGroup = useCallback(
    async (id: number, name: string, code: string): Promise<{ success: boolean; message?: string }> => {
      setIsUpdating(true);
      setError(null);
      try {
        const response = await updateEconomicGroup(id, name, code);
        if (response.success) {
          await refreshEconomicGroups();
          return { success: true };
        }
        const errorMsg = response.message || 'Error al actualizar el grupo económico';
        setError(errorMsg);
        return { success: false, message: errorMsg };
      } catch {
        const errorMsg = 'Error al actualizar el grupo económico';
        setError(errorMsg);
        return { success: false, message: errorMsg };
      } finally {
        setIsUpdating(false);
      }
    },
    [refreshEconomicGroups],
  );

  const removeGroup = useCallback(
    async (id: number): Promise<{ success: boolean; message?: string }> => {
      setIsDeleting(true);
      setError(null);
      try {
        const response = await deleteEconomicGroup(id);
        if (response.success) {
          await refreshEconomicGroups();
          return { success: true };
        }
        const errorMsg = response.message || 'Error al eliminar el grupo económico';
        setError(errorMsg);
        return { success: false, message: errorMsg };
      } catch {
        const errorMsg = 'Error al eliminar el grupo económico';
        setError(errorMsg);
        return { success: false, message: errorMsg };
      } finally {
        setIsDeleting(false);
      }
    },
    [refreshEconomicGroups],
  );

  return {
    economicGroups,
    isLoading,
    error,
    isCreating,
    isUpdating,
    isDeleting,
    refreshEconomicGroups,
    createGroup,
    updateGroup,
    removeGroup,
  };
};
